import { useState } from "react";
import { LogOut, Trophy, Gift, Home, MapPin, ShieldCheck, ChevronDown, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export function DashboardShell({ title, subtitle, children, showBell = true }) {
  const { user, logout, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [savingPw, setSavingPw] = useState(false);

  const changePw = async () => {
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPwOpen(false); setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingPw(false); }
  };

  const dashHref = user?.role === "grocery_store" ? "/store" : user?.role === "aid_organization" ? "/organization" : "/admin";

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success("Your account has been deleted.");
      navigate("/");
    } catch {
      toast.error("Could not delete your account. Please try again.");
    } finally {
      setDeleting(false);
      setDelOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/"><Logo /></Link>
            <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
              <Link data-testid="nav-dashboard" to={dashHref} className="text-stone-600 hover:text-emerald-800 transition-colors">Dashboard</Link>
              <Link data-testid="nav-home" to="/" className="flex items-center gap-1.5 text-stone-600 hover:text-emerald-800 transition-colors"><Home className="w-4 h-4" /> Home</Link>
              <Link data-testid="nav-findhelp" to="/near-me" className="flex items-center gap-1.5 text-stone-600 hover:text-emerald-800 transition-colors"><MapPin className="w-4 h-4" /> Find Help</Link>
              <Link data-testid="nav-transparency" to="/transparency" className="flex items-center gap-1.5 text-stone-600 hover:text-emerald-800 transition-colors"><ShieldCheck className="w-4 h-4" /> Transparency</Link>
              <Link data-testid="nav-leaderboard" to="/leaderboard" className="flex items-center gap-1.5 text-stone-600 hover:text-emerald-800 transition-colors"><Trophy className="w-4 h-4" /> Leaderboard</Link>
              {user?.role !== "grocery_store" && (
                <Link data-testid="nav-help" to="/help" className="flex items-center gap-1.5 text-stone-600 hover:text-emerald-800 transition-colors"><Gift className="w-4 h-4" /> Help Someone</Link>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {showBell && <NotificationBell />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 p-1.5 pl-3 rounded-full hover:bg-stone-100 transition-colors">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-stone-900 leading-tight">{user?.org_name || user?.name}</p>
                    <p className="text-xs text-stone-500 capitalize">{user?.role?.replace("_", " ")}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-stone-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate(dashHref)}>Dashboard</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}><LogOut className="w-4 h-4 mr-2" /> Log out</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-change-password" onClick={() => setPwOpen(true)}>Change password</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-delete-account" onClick={() => setDelOpen(true)} className="text-red-600 focus:text-red-600 focus:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 lg:p-8 space-y-8">
        <div className="animate-fade-up">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">{title}</h1>
          {subtitle && <p className="text-stone-500 mt-1">{subtitle}</p>}
        </div>
        {children}
      </main>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently removes your account{user?.role === "grocery_store" ? ", your listings," : user?.role === "aid_organization" ? ", your supply needs," : ""} and related data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDelOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-delete-account" onClick={doDelete} disabled={deleting} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Deleting…" : "Yes, delete my account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Change password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-stone-500">Current password</Label>
              <Input data-testid="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase text-stone-500">New password (min 8 chars)</Label>
              <Input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} className="rounded-xl h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="pw-save" onClick={changePw} disabled={savingPw} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white">{savingPw ? "Saving…" : "Update password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
